import { Component, OnInit } from '@angular/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { Util } from 'src/shared/util/util';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Keyboard } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';
import { Capacitor } from '@capacitor/core';
import { Exif as CapacitorExif } from '@capacitor-community/exif';
import { Storage } from '@ionic/storage-angular';
import OneSignal from 'onesignal-cordova-plugin';
import { VideoRecorder, VideoRecorderCamera } from '@capacitor-community/video-recorder';

/**
 * Hidden test page for Capacitor native features
 * Access via 10 taps on dashboard logo/title
 * Used for e2e testing of native mobile capabilities
 */
@Component({
  selector: 'page-capacitor-test',
  templateUrl: 'capacitor-test.html',
  styleUrls: ['capacitor-test.scss'],
  standalone: false
})
export class CapacitorTestPage implements OnInit {

  // Test results displayed on page
  results = {
    camera: '',
    cameraMultiple: '',
    gallery: '',
    geolocation: '',
    filePicker: '',
    appInfo: '',
    keyboard: '',
    browser: '',
    platform: '',
    exif: '',
    storage: '',
    notifications: '',
    splashScreen: '',
    videoRecorder: '',
  };

  // Test metadata for validation
  testMetadata = {
    camera: { format: '', width: 0, height: 0, hasBase64: false },
    geolocation: { accuracy: 0, altitude: null, speed: null },
    platform: { isNative: false, platform: '' },
  };

  // Selected images
  selectedPhoto: string | null = null;
  selectedPhotos: string[] = [];
  selectedFile: string | null = null;

  private storage: Storage;

  constructor(
    public util: Util,
    storageService: Storage
  ) {
    this.storage = storageService;
  }

  async ngOnInit() {
    console.log('Capacitor Test Page loaded');
    // Clear any previous photos
    this.selectedPhoto = null;
    this.selectedPhotos = [];
    this.selectedFile = null;
    // Initialize storage
    await this.storage.create();
    // Auto-run platform detection test
    await this.testPlatformInfo();
  }

  /**
   * Test Camera capability with validation
   * AUTOMATED: Uses gallery (no user interaction needed if photo exists)
   */
  async testCamera() {
    try {
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos  // Changed from Camera to Photos for automation
      });

      // Validate response structure
      this.testMetadata.camera = {
        format: photo.format || 'unknown',
        width: 0,
        height: 0,
        hasBase64: !!photo.base64String
      };

      this.selectedPhoto = photo.dataUrl || null;

      // Deterministic validation
      const validations = [
        { test: !!photo.dataUrl, message: 'Has dataUrl' },
        { test: photo.format !== undefined, message: 'Has format' },
        { test: (photo.dataUrl?.startsWith('data:image/') || false), message: 'Valid data URL format' },
      ];

      const passed = validations.filter(v => v.test).length;
      const failed = validations.filter(v => !v.test).map(v => v.message);

      this.results.camera = `Camera test: ${passed}/${validations.length} checks passed\n` +
        `Format: ${photo.format}\n${ 
        failed.length > 0 ? `Failed: ${failed.join(', ')}` : 'All validations passed ✓'}`;

      this.util.showSuccess('Camera test completed');
    } catch (error) {
      this.results.camera = `Camera error: ${error}`;
      this.util.showError('Camera test failed');
      console.error('Camera error:', error);
    }
  }

  /**
   * Test Photo Gallery capability
   */
  async testGallery() {
    try {
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos
      });

      this.selectedPhoto = photo.dataUrl || null;
      this.results.gallery = 'Gallery photo selected successfully';
      this.util.showSuccess('Gallery test passed');
    } catch (error) {
      this.results.gallery = `Gallery error: ${error}`;
      this.util.showError('Gallery test failed');
      console.error('Gallery error:', error);
    }
  }

  /**
   * Test File Picker capability
   */
  async testFilePicker() {
    try {
      const result = await FilePicker.pickFiles({
        readData: true
      });

      if (result.files && result.files.length > 0) {
        const file = result.files[0];
        this.selectedFile = file.name;
        this.results.filePicker = `File selected: ${file.name} (${file.size} bytes)`;
        this.util.showSuccess('File picker test passed');
      }
    } catch (error) {
      this.results.filePicker = `File picker error: ${error}`;
      this.util.showError('File picker test failed');
      console.error('File picker error:', error);
    }
  }

  /**
   * Test multiple photo selection
   */
  async testMultiplePhotos() {
    try {
      const photos = await Camera.pickImages({
        quality: 75,
        limit: 5
      });

      this.selectedPhotos = [];
      for (const photo of photos.photos) {
        if (photo.webPath) {
          this.selectedPhotos.push(photo.webPath);
        }
      }

      const validations = [
        { test: photos.photos.length > 0, message: 'Has photos' },
        { test: photos.photos.length <= 5, message: 'Respects limit' },
        { test: photos.photos.every(p => p.webPath || p.path), message: 'All have paths' },
      ];

      const passed = validations.filter(v => v.test).length;

      this.results.cameraMultiple = `Multiple photos: ${passed}/${validations.length} checks passed\n` +
        `Selected ${photos.photos.length} photos`;

      this.util.showSuccess('Multiple photos test completed');
    } catch (error) {
      this.results.cameraMultiple = `Multiple photos error: ${error}`;
      console.error('Multiple photos error:', error);
    }
  }

  /**
   * Test Geolocation with full validation
   */
  async testGeolocation() {
    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      });

      // Store metadata for validation
      this.testMetadata.geolocation = {
        accuracy: position.coords.accuracy,
        altitude: position.coords.altitude,
        speed: position.coords.speed
      };

      // Deterministic validations
      const validations = [
        { test: typeof position.coords.latitude === 'number', message: 'Has latitude' },
        { test: typeof position.coords.longitude === 'number', message: 'Has longitude' },
        { test: position.coords.latitude >= -90 && position.coords.latitude <= 90, message: 'Valid latitude range' },
        { test: position.coords.longitude >= -180 && position.coords.longitude <= 180, message: 'Valid longitude range' },
        { test: typeof position.coords.accuracy === 'number', message: 'Has accuracy' },
        { test: typeof position.timestamp === 'number', message: 'Has timestamp' },
      ];

      const passed = validations.filter(v => v.test).length;
      const failed = validations.filter(v => !v.test).map(v => v.message);

      this.results.geolocation = `Geolocation test: ${passed}/${validations.length} checks passed\n` +
        `Lat: ${position.coords.latitude.toFixed(6)}, Lon: ${position.coords.longitude.toFixed(6)}\n` +
        `Accuracy: ${position.coords.accuracy.toFixed(2)}m\n${ 
        failed.length > 0 ? `Failed: ${failed.join(', ')}` : 'All validations passed ✓'}`;

      this.util.showSuccess('Geolocation test passed');
    } catch (error) {
      this.results.geolocation = `Geolocation error: ${error}`;
      this.util.showError('Geolocation test failed');
      console.error('Geolocation error:', error);
    }
  }

  /**
   * Test App Info API
   */
  async testAppInfo() {
    try {
      const appInfo = await App.getInfo();

      const validations = [
        { test: !!appInfo.name, message: 'Has app name' },
        { test: !!appInfo.id, message: 'Has app ID' },
        { test: !!appInfo.version, message: 'Has version' },
        { test: !!appInfo.build, message: 'Has build number' },
      ];

      const passed = validations.filter(v => v.test).length;

      this.results.appInfo = `App Info: ${passed}/${validations.length} checks passed\n` +
        `Name: ${appInfo.name}\nID: ${appInfo.id}\nVersion: ${appInfo.version}\nBuild: ${appInfo.build}`;

      this.util.showSuccess('App info test passed');
    } catch (error) {
      this.results.appInfo = `App info error: ${error}`;
      console.error('App info error:', error);
    }
  }

  /**
   * Test Keyboard API
   */
  async testKeyboard() {
    try {
      if (!Capacitor.isNativePlatform()) {
        this.results.keyboard = 'Keyboard API only available on native platforms';
        return;
      }

      // Show keyboard
      await Keyboard.show();
      await new Promise(resolve => setTimeout(resolve, 500));

      // Hide keyboard
      await Keyboard.hide();

      this.results.keyboard = 'Keyboard show/hide successful ✓';
      this.util.showSuccess('Keyboard test passed');
    } catch (error) {
      this.results.keyboard = `Keyboard error: ${error}`;
      console.error('Keyboard error:', error);
    }
  }

  /**
   * Test Browser API
   */
  async testBrowser() {
    try {
      // Open a URL (will close immediately in test)
      await Browser.open({ url: 'https://example.com', presentationStyle: 'popover' });

      // Close immediately for testing
      setTimeout(async () => {
        await Browser.close();
      }, 500);

      this.results.browser = 'Browser open/close successful ✓';
      this.util.showSuccess('Browser test passed');
    } catch (error) {
      this.results.browser = `Browser error: ${error}`;
      console.error('Browser error:', error);
    }
  }

  /**
   * Test Platform Info (auto-runs on init)
   */
  async testPlatformInfo() {
    try {
      const platform = Capacitor.getPlatform();
      const isNative = Capacitor.isNativePlatform();

      this.testMetadata.platform = {
        isNative,
        platform
      };

      const validations = [
        { test: typeof platform === 'string', message: 'Has platform' },
        { test: ['ios', 'android', 'web'].includes(platform), message: 'Valid platform value' },
        { test: typeof isNative === 'boolean', message: 'Has isNative flag' },
      ];

      const passed = validations.filter(v => v.test).length;

      this.results.platform = `Platform: ${passed}/${validations.length} checks passed\n` +
        `Platform: ${platform}\nIs Native: ${isNative}`;

    } catch (error) {
      this.results.platform = `Platform error: ${error}`;
      console.error('Platform error:', error);
    }
  }

  /**
   * Test EXIF data reading
   */
  async testExifData() {
    try {
      // First, let user pick a photo to test EXIF reading
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Photos
      });

      if (!photo.path) {
        this.results.exif = 'Photo selected but no path available for EXIF reading';
        return;
      }

      // Read EXIF data from the photo
      const exifData = await CapacitorExif.getCoordinates({ pathToImage: photo.path });

      // Validate EXIF data structure
      const validations = [
        { test: exifData !== null && exifData !== undefined, message: 'EXIF data returned' },
        { test: typeof exifData === 'object', message: 'EXIF data is object' },
        { test: Object.keys(exifData).length > 0, message: 'Has EXIF fields' },
      ];

      const passed = validations.filter(v => v.test).length;

      // Extract common EXIF fields
      const fields = {
        make: exifData['Make'] || 'N/A',
        model: exifData['Model'] || 'N/A',
        datetime: exifData['DateTime'] || exifData['DateTimeOriginal'] || 'N/A',
        latitude: exifData['GPSLatitude'] || 'N/A',
        longitude: exifData['GPSLongitude'] || 'N/A',
        orientation: exifData['Orientation'] || 'N/A',
        totalFields: Object.keys(exifData).length
      };

      this.results.exif = `EXIF test: ${passed}/${validations.length} checks passed\n` +
        `Camera: ${fields.make} ${fields.model}\n` +
        `Date: ${fields.datetime}\n` +
        `GPS: ${fields.latitude}, ${fields.longitude}\n` +
        `Orientation: ${fields.orientation}\n` +
        `Total EXIF fields: ${fields.totalFields}`;

      this.util.showSuccess('EXIF test completed');
    } catch (error) {
      this.results.exif = `EXIF error: ${error}\nNote: Select a photo with EXIF data (e.g., iPhone camera photo)`;
      this.util.showError('EXIF test failed');
      console.error('EXIF error:', error);
    }
  }

  /**
   * Test Video Recorder
   * AUTOMATED: Tests initialize/destroy without actual recording
   */
  async testVideoRecorder() {
    try {
      if (!Capacitor.isNativePlatform()) {
        this.results.videoRecorder = 'Video Recorder only available on native platforms';
        return;
      }

      // Test 1: Initialize
      await VideoRecorder.initialize({
        camera: VideoRecorderCamera.BACK,
        previewFrames: [] // No preview for automated testing
      });

      // Test 2: Destroy (cleanup)
      await VideoRecorder.destroy();

      const validations = [
        { test: true, message: 'Initialize successful' },
        { test: true, message: 'Destroy successful' },
        { test: typeof VideoRecorder.startRecording === 'function', message: 'Has startRecording method' },
        { test: typeof VideoRecorder.stopRecording === 'function', message: 'Has stopRecording method' },
      ];

      const passed = validations.filter(v => v.test).length;

      this.results.videoRecorder = `Video Recorder: ${passed}/${validations.length} checks passed\n` +
        `API: Available and functional ✓\n` +
        `Note: Full recording test requires manual interaction`;

      this.util.showSuccess('Video Recorder test passed');
    } catch (error) {
      this.results.videoRecorder = `Video Recorder error: ${error}`;
      this.util.showError('Video Recorder test failed');
      console.error('Video Recorder error:', error);
    }
  }

  /**
   * Test Ionic Storage
   */
  async testStorage() {
    try {
      const testKey = 'capacitor_test_key';
      const testValue = { data: 'test', timestamp: Date.now() };

      // Test write
      await this.storage.set(testKey, testValue);

      // Test read
      const retrieved = await this.storage.get(testKey);

      // Test keys
      const keys = await this.storage.keys();

      // Test delete
      await this.storage.remove(testKey);
      const afterDelete = await this.storage.get(testKey);

      const validations = [
        { test: retrieved !== null, message: 'Can write and read' },
        { test: retrieved?.data === testValue.data, message: 'Data matches' },
        { test: keys.includes(testKey), message: 'Key exists in keys list' },
        { test: afterDelete === null, message: 'Can delete' },
      ];

      const passed = validations.filter(v => v.test).length;

      this.results.storage = `Ionic Storage: ${passed}/${validations.length} checks passed\n` +
        `Supported: ${await this.storage.driver}\n` +
        `Test key deleted successfully`;

      this.util.showSuccess('Storage test passed');
    } catch (error) {
      this.results.storage = `Storage error: ${error}`;
      console.error('Storage error:', error);
    }
  }

  /**
   * Test Push Notifications (OneSignal)
   */
  async testNotifications() {
    try {
      if (!Capacitor.isNativePlatform()) {
        this.results.notifications = 'Notifications only available on native platforms';
        return;
      }

      // Check if OneSignal is initialized
      const isInitialized = typeof OneSignal !== 'undefined';

      // Get permission state (doesn't require prompting)
      let permissionState = 'unknown';
      try {
        const permission = await OneSignal.Notifications.getPermissionAsync();
        permissionState = permission ? 'granted' : 'denied';
      } catch (_e) {
        permissionState = 'error checking';
      }

      const validations = [
        { test: isInitialized, message: 'OneSignal SDK loaded' },
        { test: typeof OneSignal.Notifications !== 'undefined', message: 'Notifications API available' },
      ];

      const passed = validations.filter(v => v.test).length;

      this.results.notifications = `OneSignal: ${passed}/${validations.length} checks passed\n` +
        `SDK Initialized: ${isInitialized}\n` +
        `Permission: ${permissionState}\n` +
        `Note: Full testing requires user permission`;

      this.util.showSuccess('Notifications test completed');
    } catch (error) {
      this.results.notifications = `Notifications error: ${error}`;
      console.error('Notifications error:', error);
    }
  }

  /**
   * Test Splash Screen API
   */
  async testSplashScreen() {
    try {
      if (!Capacitor.isNativePlatform()) {
        this.results.splashScreen = 'Splash Screen only available on native platforms';
        return;
      }

      // Show splash screen briefly
      await SplashScreen.show({
        autoHide: false
      });

      await new Promise(resolve => setTimeout(resolve, 500));

      // Hide splash screen
      await SplashScreen.hide();

      this.results.splashScreen = 'Splash Screen show/hide successful ✓';
      this.util.showSuccess('Splash screen test passed');
    } catch (error) {
      this.results.splashScreen = `Splash Screen error: ${error}`;
      console.error('Splash Screen error:', error);
    }
  }

  /**
   * Run all tests sequentially - FULLY AUTOMATED
   */
  async runAllTests() {
    this.util.showInfo('Running all automated tests...');

    // Core tests (no user interaction)
    await this.testPlatformInfo();
    await this.testAppInfo();
    await this.testStorage();
    await this.testGeolocation();
    await this.testNotifications();
    await this.testKeyboard();
    await this.testSplashScreen();
    await this.testBrowser();
    await this.testVideoRecorder();

    // Media tests (automated if assets pre-loaded)
    await this.testCamera();          // Uses gallery (automated)
    await this.testGallery();         // Uses gallery (automated)
    await this.testMultiplePhotos();  // Uses gallery (automated)
    await this.testFilePicker();      // Automated if file exists
    await this.testExifData();        // Automated if photo exists

    this.util.showSuccess('All automated tests completed!');
  }

  /**
   * Clear all test results
   */
  clearResults() {
    this.results = {
      camera: '',
      cameraMultiple: '',
      gallery: '',
      geolocation: '',
      filePicker: '',
      appInfo: '',
      keyboard: '',
      browser: '',
      platform: '',
      exif: '',
      storage: '',
      notifications: '',
      splashScreen: '',
      videoRecorder: '',
    };
    this.selectedPhoto = null;
    this.selectedPhotos = [];
    this.selectedFile = null;
    this.testMetadata = {
      camera: { format: '', width: 0, height: 0, hasBase64: false },
      geolocation: { accuracy: 0, altitude: null, speed: null },
      platform: { isNative: false, platform: '' },
    };
    this.util.showInfo('Results cleared');
  }

  /**
   * Export test results as JSON for validation/comparison
   */
  exportTestResults(): string {
    return JSON.stringify({
      timestamp: new Date().toISOString(),
      results: this.results,
      metadata: this.testMetadata,
      platform: Capacitor.getPlatform(),
      isNative: Capacitor.isNativePlatform()
    }, null, 2);
  }
}
